import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  FormControl,
  FormHelperText,
  Collapse,
  Link,
  HStack,
} from "@chakra-ui/react";
import { FaChevronDown, FaExclamationCircle } from "react-icons/fa";
import { FaMoneyBills } from "react-icons/fa6";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { getFormError } from "../../i18n";
import Button from "../../components/Button";
import MinAmountInfo from "../../components/MinAmountInfo";

const MAX_FIELD_LENGTH = 20;
const MIN_FIELD_LENGTH = 1;

const MabarField = ({ label, placeholder, value, onChange, required, error, _ }) => {
  const [touched, setTouched] = useState(false);
  const showError = error && (value.length > 0 || touched);
  return (
  <FormControl flex="1" isInvalid={!!showError}>
    <Text
      color="black"
      fontSize="16px"
      fontFamily="IBM Plex Mono"
      fontWeight="500"
      lineHeight="24px"
    >
      {label}{required && <Text as="span" color="red.500"> *</Text>}
    </Text>
    <Input
      variant="flushed"
      borderColor={showError ? "red.500" : "black"}
      fontSize="16px"
      fontFamily="IBM Plex Mono"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={() => setTouched(true)}
      maxLength={MAX_FIELD_LENGTH}
    />
    <Flex justify="space-between">
      <FormHelperText color={showError ? "red.500" : "#929393"} fontSize="12px" fontFamily="IBM Plex Mono">
        {showError ? getFormError(error, _) : `Min. ${MIN_FIELD_LENGTH} karakter`}
      </FormHelperText>
      <FormHelperText color="#929393" fontSize="12px" fontFamily="IBM Plex Mono">
        {value.length}/{MAX_FIELD_LENGTH}
      </FormHelperText>
    </Flex>
  </FormControl>
  );
};

const MabarSelector = ({ mabarSetting, values, setFieldValue, currency, errors = {}, disabled = false }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const { _ } = useLingui();

  if (!mabarSetting || !mabarSetting.is_active) return null;

  const { mabar_types, rules: mabar_rules, game_id_label, custom_fields } = mabarSetting;
  const fields = custom_fields?.fields || [];

  const amountNum = Number(values.amount) || 0;
  const hasAmount = values.amount !== "" && values.amount !== undefined;
  const bundleReguler = mabar_types?.mabar_bundle_reguler || 0;
  const bundleVip = mabar_types?.mabar_bundle_vip || 0;

  // disabled: a challenge is selected (can't be combined with mabar) or amount below the type's min
  const types = [
    {
      key: "mabar_regular",
      label: "Regular",
      color: "saweria-yellow.normal",
      enabled: mabar_types?.mabar_regular,
      min: mabar_types?.mabar_regular_min_donation || 0,
      minLabel: "Min. mabar regular:",
    },
    {
      key: "mabar_vip",
      label: "Fast Track",
      color: "saweria-green.normal",
      enabled: mabar_types?.mabar_vip,
      min: mabar_types?.mabar_vip_min_donation || 0,
      minLabel: "Min. mabar fast track:",
    },
    {
      key: "mabar_bundle",
      label: "Bundle",
      color: "saweria-pink.normal",
      enabled: mabar_types?.mabar_bundle && bundleReguler + bundleVip > 0,
      min: mabar_types?.min_bundle_price || 0,
      minLabel: "Min. Bundle:",
    },
  ]
    .filter((type) => type.enabled)
    .map((type) => ({ ...type, disabled: disabled || (hasAmount && amountNum < type.min) }));

  if (types.length === 0) return null;

  const selected = types.find((type) => type.key === selectedType);
  const belowMin = selected && selected.min && Number(values.amount) < selected.min;

  // ponytail: only show mins not yet met by current amount
  const unmetTypes = types.filter((type) => amountNum < type.min);
  const showMinInfo = hasAmount && !selectedType && unmetTypes.length > 0;

  // ponytail: auto-deselect + clear fields if selected type no longer meets min
  const selectedNowDisabled = Boolean(selected?.disabled);

  useEffect(() => {
    if (selectedNowDisabled && selectedType) {
      setSelectedType(null);
      setFieldValue("mabar_type", null);
      setFieldValue("mabar_game_id", "");
      setFieldValue("mabar_custom_fields_1", "");
      setFieldValue("mabar_custom_fields_2", "");
    }
  }, [selectedNowDisabled]);

  const bundleTitle = [
    bundleReguler > 0 && `${bundleReguler} Regular`,
    bundleVip > 0 && `${bundleVip} Fast Track`,
  ].filter(Boolean).join(" & ");
  const formTitle = selectedType === "mabar_bundle"
    ? `🎮 Paket ${bundleTitle}`
    : "🎮 Mabar bareng aku, yuk!";

  const handleTypeSelect = (type) => {
    if (selectedType === type) {
      setSelectedType(null);
      setFieldValue("mabar_type", null);
      setFieldValue("mabar_game_id", "");
      setFieldValue("mabar_custom_fields_1", "");
      setFieldValue("mabar_custom_fields_2", "");
      return;
    }
    setSelectedType(type);
    setFieldValue("mabar_type", type);
  };

  const handleFieldChange = (fieldName) => (e) => {
    const val = e.target.value.slice(0, MAX_FIELD_LENGTH);
    setFieldValue(fieldName, val);
  };

  // ponytail: map custom_fields to formik field keys by index
  const FIELD_KEYS = ["mabar_custom_fields_1", "mabar_custom_fields_2"];
  const allFields = [
    { label: game_id_label || "Game ID", key: "mabar_game_id", required: true, placeholder: "Contoh: 12345" },
    ...fields.map((f, i) => ({
      label: f.label,
      key: FIELD_KEYS[i],
      required: f.required,
      placeholder: i === 0 ? "Contoh: 12345" : "Contoh: budiman",
    })),
  ];

  return (
    <FormControl mb="24px">
      <Text fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" mb={2}>
        <Trans>Mabar:</Trans>
      </Text>

      {/* Type buttons */}
      <Flex gap="1em" flexWrap="wrap">
        {types.map((type) => (
          <Button
            key={type.key}
            type="button"
            pressed={selectedType === type.key}
            label={type.label}
            mb={1}
            backgroundColor={type.color}
            disabled={type.disabled}
            filter={type.disabled ? "grayscale(75%)" : "none"}
            onClick={() => {
              if (type.disabled) return;
              handleTypeSelect(type.key);
            }}
          />
        ))}
      </Flex>

      {/* Min info - always visible like mediashare */}
      {showMinInfo && (
        <MinAmountInfo
          labels={unmetTypes.map((type) => type.minLabel)}
          amounts={unmetTypes.map((type) => type.min)}
          currency={currency}
          reactIcon={FaMoneyBills}
        />
      )}

      {/* Form - shown when type selected */}
      {selectedType && belowMin && (
        <MinAmountInfo
          labels={[selected.minLabel]}
          amounts={[selected.min]}
          currency={currency}
          reactIcon={FaMoneyBills}
        />
      )}
      {selectedType && !belowMin && (
        <Box
          width="100%"
          padding="20px"
          background="#EAEFED"
          borderRadius="6.39px"
          outline="1.07px #BEC1C1 solid"
          outlineOffset="-1.07px"
          display="flex"
          flexDirection="column"
          gap="16px"
        >
          {/* Header */}
          <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} width="100%" gap={{ base: "4px", md: "0" }}>
            <Text fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" lineHeight="24px" color="black" flex={1}>
              {formTitle}
            </Text>
            {mabar_rules && (
              <HStack spacing="4px">
                <Link
                  href="#"
                  fontFamily="IBM Plex Mono"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color="#3182CE"
                  textDecoration="none"
                  _hover={{ textDecoration: "underline" }}
                  onClick={(e) => { e.preventDefault(); setShowRules((v) => !v); }}
                >
                  Lihat aturan
                </Link>
                <FaChevronDown size={16} color="#3182CE" />
              </HStack>
            )}
          </Flex>

          {/* Rules collapse */}
          {mabar_rules && (
            <Collapse in={showRules} animateOpacity>
              <Box
                width="100%"
                px="16px"
                py="12px"
                bg="#DCEEFF"
                borderRadius="6px"
                outline="1px solid #3182CE"
                outlineOffset="-1px"
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                gap="12px"
                overflow="hidden"
                my="10px"
              >
                {/* Info Icon */}
                <Box width="30px" height="20px" flexShrink={0} position="relative" overflow="hidden">
                  <FaExclamationCircle size={20} color="#3182CE" />
                </Box>

                {/* Content */}
                <Flex flex={1} direction="column" gap="8px">
                  <Text
                    color="black"
                    fontSize="16px"
                    fontFamily="IBM Plex Mono"
                    fontWeight="700"
                    lineHeight="24px"
                    wordBreak="break-word"
                  >
                    Aturan Mabar:
                  </Text>
                  <Text
                    color="black"
                    fontSize="16px"
                    fontFamily="IBM Plex Mono"
                    fontWeight="400"
                    lineHeight="24px"
                    wordBreak="break-word"
                    whiteSpace="pre-wrap"
                  >
                    {mabar_rules}
                  </Text>
                </Flex>
              </Box>
            </Collapse>
          )}

          {/* Fields */}
          <Flex gap="16px" direction={{ base: "column", md: "row" }} flexWrap="wrap">
            {allFields.map((f) => (
              <MabarField
                key={f.key}
                label={f.label}
                placeholder={f.placeholder}
                value={values[f.key] || ""}
                onChange={handleFieldChange(f.key)}
                required={f.required}
                error={errors[f.key]}
                _={_}
              />
            ))}
          </Flex>
        </Box>
      )}
    </FormControl>
  );
};

export { MabarSelector };
